import React from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {useTranslation} from 'react-i18next';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementSequenceQuestion} from '../../../../../incase-domain/elements/question/questionSequence';
import {shuffleElementsOrder, swapArrayIndices} from '../../../../../utils/utils';
import SectionTitle from '../../../../elements/common/SectionTitle';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';

interface ISequenceCorrectOrderProps {
    highlightedElement: IElementSequenceQuestion;
    highlightedElementChangeHandler: {
        single: (field: keyof IElementSequenceQuestion | string, value: unknown) => void;
        multiple: (fields: (keyof IElementSequenceQuestion)[], values: unknown[]) => void;
    };
}

export default function SequencePreviewOrder(props: ISequenceCorrectOrderProps): JSX.Element {
    const {highlightedElement, highlightedElementChangeHandler} = props;
    const {t} = useTranslation();

    function swapSequenceItemOrderHandler(newIndex: number, oldIndex: number): void {
        const newSequence = swapArrayIndices(highlightedElement.sequence, newIndex, oldIndex);
        highlightedElementChangeHandler.single('sequence', newSequence);
    }

    return (
        <div className="l-group--form l-group__item">
            <SectionTitle label={t('presEditPage.editSidebar.elements.sequenceQuestion.initialOrder')} />

            <div className="u-mb-2 u-flex u-v-center" style={{fontSize: '.75rem'}}>
                <input
                    checked={highlightedElement.automaticShuffle}
                    onChange={(event) => {
                        if (event.target.checked) {
                            highlightedElementChangeHandler.multiple(
                                ['automaticShuffle', 'sequence'],
                                [true, shuffleElementsOrder(highlightedElement.sequence)]
                            );
                        } else {
                            highlightedElementChangeHandler.single('automaticShuffle', false);
                        }
                    }}
                    type="checkbox"
                    id="shuffle-answers-order"
                />
                <label className="u-ml-1" htmlFor="shuffle-answers-order">
                    {t('presEditPage.editSidebar.elements.sequenceQuestion.automaticShuffle')}
                </label>
            </div>

            {!highlightedElement.automaticShuffle && (
                <DragDropContext
                    onDragEnd={(e) => {
                        if (typeof e.destination?.index !== 'undefined') swapSequenceItemOrderHandler(e.destination.index, e.source.index);
                    }}
                >
                    <Droppable droppableId={`correct-sequence-${highlightedElement.id}`}>
                        {(provided) => {
                            return (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {highlightedElement.sequence.map((sequence, index) => {
                                        return (
                                            <Draggable
                                                key={`${index}-seq-init-${sequence.text}`}
                                                draggableId={`${index}-seq-init-${sequence.text}`}
                                                index={index}
                                            >
                                                {(provided) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            key={`highlighted-element-${sequence.text}-correct-seq-item-${index}`}
                                                            className="u-mb-1 c-sequenceQuestionOrder__container"
                                                        >
                                                            <DebouncedTextInput
                                                                className="c-sidepanel__input"
                                                                value={sequence.text}
                                                                onChange={() => {
                                                                    return null;
                                                                }}
                                                                disabled
                                                            />
                                                            <div className="u-absolute" style={{inset: 0}} />

                                                            <Button
                                                                variant="ghost"
                                                                disableHover
                                                                className="c-sequenceQuestionOrder"
                                                                padded={false}
                                                                trailing="material-symbols:drag-indicator"
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            );
                        }}
                    </Droppable>
                </DragDropContext>
            )}
        </div>
    );
}
